import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';

const initialState = {
  user: null,
  packages: [],
  dateSlots: [],
  dashboardMetrics: null,
  isLoading: false,
  error: null,
  toast: {
    isOpen: false,
    type: 'success',
    message: '',
  },
};

const appReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_PACKAGES':
      return { ...state, packages: action.payload };
    case 'ADD_PACKAGE':
      return { ...state, packages: [...state.packages, action.payload] };
    case 'UPDATE_PACKAGE':
      return {
        ...state,
        packages: state.packages.map(pkg => 
          pkg.id === action.payload.id ? action.payload : pkg
        ),
      };
    case 'DELETE_PACKAGE':
      return {
        ...state,
        packages: state.packages.filter(pkg => pkg.id !== action.payload),
      };
    case 'SET_DATE_SLOTS':
      return { ...state, dateSlots: action.payload };
    case 'ADD_DATE_SLOT':
      return { ...state, dateSlots: [...state.dateSlots, action.payload] };
    case 'UPDATE_DATE_SLOT':
      return {
        ...state,
        dateSlots: state.dateSlots.map(slot => 
          slot.id === action.payload.id ? action.payload : slot
        ),
      };
    case 'DELETE_DATE_SLOT':
      return {
        ...state,
        dateSlots: state.dateSlots.filter(slot => slot.id !== action.payload),
      };
    case 'SET_DASHBOARD_METRICS':
      return { ...state, dashboardMetrics: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SHOW_TOAST':
      return { ...state, toast: { isOpen: true, type: action.payload.type, message: action.payload.message } };
    case 'HIDE_TOAST':
      return { ...state, toast: { isOpen: false, type: 'success', message: '' } };
    case 'LOGOUT':
      return {
        ...initialState,
        user: null,
      };
    default:
      return state;
  }
};

const AppContext = createContext(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    // Check if user is already logged in
    const user = authService.getCurrentUser();
    if (user && authService.isAuthenticated()) {
      dispatch({ type: 'SET_USER', payload: user });
    }
  }, []);

  const login = async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      const response = await authService.login({ email, password });
      dispatch({ type: 'SET_USER', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.response?.data?.message || 'Login failed' });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = () => {
    authService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  const showToast = (type, message) => {
    dispatch({ type: 'SHOW_TOAST', payload: { type, message } });
  };

  const hideToast = () => {
    dispatch({ type: 'HIDE_TOAST' });
  };

  const value = {
    state,
    dispatch,
    login,
    logout,
    showToast,
    hideToast,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

